import { ArrowLeft, Route } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { StudyShell } from './StudyShell';
import {
  SYNTHESIS_DESIGN_TOOL_IDENTITY,
  SYNTHESIS_MODEL_BOUNDARIES,
  SYNTHESIS_MODES,
  SYNTHESIS_SOURCES,
  SYNTHESIS_YEAR_MODES,
  type SynthesisMode,
} from './synthesisDesign';
import { SynthesisDesignGreenMode } from './SynthesisDesignGreenMode';
import { SynthesisDesignPathwayMode } from './SynthesisDesignPathwayMode';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<SynthesisMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  pathway: {
    tab: 'Pathway',
    eyebrow: 'Multi-step synthesis',
    title: 'Plan a route from a starting molecule to a target',
    description:
      'Choose one reaction at a time from the Organic Builder families, then predict the overall yield from the step yields.',
  },
  green: {
    tab: 'Green chemistry',
    eyebrow: 'Compare routes',
    title: 'Which route wastes fewer atoms, and is that the whole story?',
    description:
      'Predict and calculate atom economy from balanced equations, then decide which green chemistry principle matters most.',
  },
};

export function SynthesisDesignLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<SynthesisMode>(() => modeForYear(SYNTHESIS_YEAR_MODES, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'synthesis-design', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-mint/30 bg-gradient-to-br from-wash-mint via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:p-8"
        data-tool-key={SYNTHESIS_DESIGN_TOOL_IDENTITY.toolKey}
        data-tool-version={SYNTHESIS_DESIGN_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-mint">
              <Route className="h-4 w-4" aria-hidden="true" /> Organic chemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Synthesis Design Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Chain organic reactions into a route, see how step yields multiply, and compare
              routes by atom economy and green chemistry principles.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 11–12 · senior chemistry
          </div>
        </div>

        <div
          className="mt-6 grid max-w-md grid-cols-2 rounded-full bg-white/75 p-1"
          role="group"
          aria-label="Choose a mode"
        >
          {SYNTHESIS_MODES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'pathway' ? <SynthesisDesignPathwayMode /> : <SynthesisDesignGreenMode />}

      <section
        className="mt-6 rounded-3xl border border-brand-sunshine/40 bg-wash-sunshine p-5"
        aria-label="Model boundaries"
      >
        <p className="text-sm font-extrabold text-ink">Model boundaries</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
          {SYNTHESIS_MODEL_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-bold leading-5 text-slate2">
          Sources: {SYNTHESIS_SOURCES.organic.label}; {SYNTHESIS_SOURCES.anastas.label};{' '}
          {SYNTHESIS_SOURCES.trost.label}; {SYNTHESIS_SOURCES.industrial.label}. {SYNTHESIS_SOURCES.yields.detail} Values
          are pending teaching review.
        </p>
      </section>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. It is not a laboratory method, does not
        replace a supervised practical or risk assessment, saves no attempt and does not claim
        alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
